import { Button } from '@/components/ui/button'
import {
	Drawer,
	DrawerClose,
	DrawerContentRight,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { useWorkspaceUrl } from '@/hooks/useWorkspaceUrl'
import { CreditCard, LayoutGrid, Plus, Table2, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface BillingProfile {
	id: string
	name: string
	email: string
	paymentMethod: string
	createdAt: string
}

function BillingProfilesTable({
	profiles,
	onSelect,
	onAdd,
	selectedId,
	hasSubscription = false,
}: {
	profiles: BillingProfile[]
	onSelect: (profile: BillingProfile) => void
	onAdd: () => void
	selectedId?: string
	hasSubscription?: boolean
}) {
	const navigate = useNavigate()
	const workspaceUrl = useWorkspaceUrl()
	const handleSignUp = () => {
		navigate(`${workspaceUrl}/sign-up`)
	}
	return (
		<Table>
			<TableHeader className="border-t border-t-border border-b border-b-border [&_tr]:!border-0">
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Email</TableHead>
					<TableHead>Payment Method</TableHead>
					<TableHead>Created</TableHead>
				</TableRow>
			</TableHeader>
		<TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-b-border">
				{profiles.length === 0 ? (
							<TableRow className="!border-b !border-b-border">
						<TableCell colSpan={4} className="h-32 p-0">
							<div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-full py-8">
								<CreditCard className="h-8 w-8 text-foreground/60" />
								<p className="text-sm font-medium text-muted-foreground">No billing profiles yet</p>
								{hasSubscription ? (
                                    <Button variant="outline" onClick={onAdd}>
										<Plus className="h-4 w-4 mr-2" />
										Add Billing Profile
									</Button>
								) : (
                                    <Button variant="outline" onClick={handleSignUp}>
										Sign Up
									</Button>
								)}
							</div>
						</TableCell>
					</TableRow>
				) : (
					profiles.map((profile) => (
						<TableRow
							key={profile.id}
							className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-border ${
								profile.id === selectedId ? 'bg-muted' : ''
							}`}
							onClick={() => onSelect(profile)}
						>
							<TableCell>{profile.name}</TableCell>
							<TableCell>{profile.email}</TableCell>
							<TableCell>{profile.paymentMethod}</TableCell>
							<TableCell>{profile.createdAt}</TableCell>
						</TableRow>
					))
				)}
			</TableBody>
		</Table>
	)
}

function BillingProfilesCards({
	profiles,
	onSelect,
	onAdd,
	selectedId,
	hasSubscription = false,
}: {
	profiles: BillingProfile[]
	onSelect: (profile: BillingProfile) => void
	onAdd: () => void
	selectedId?: string
	hasSubscription?: boolean
}) {
	const navigate = useNavigate()
	const workspaceUrl = useWorkspaceUrl()
	
	const handleSignUp = () => {
		navigate(`${workspaceUrl}/sign-up`)
	}
	if (profiles.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-40 py-8">
				<div className="h-8 w-8 shrink-0 flex items-center justify-center">
					<CreditCard className="h-8 w-8 text-foreground/60" />
				</div>
				<p className="text-sm font-medium text-muted-foreground">No billing profiles yet</p>
				{hasSubscription ? (
                    <Button variant="outline" onClick={onAdd}>
						<Plus className="h-4 w-4 mr-2" />
						Add Billing Profile
					</Button>
				) : (
                    <Button variant="outline" onClick={handleSignUp}>
						Sign Up
					</Button>
				)}
			</div>
		)
	}

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			{profiles.map((profile) => (
				<div
					key={profile.id}
					className={`border border-[#3A2E2F]/20 rounded-md p-4 cursor-pointer ${
						profile.id === selectedId ? 'ring-2 ring-primary' : ''
					}`}
					onClick={() => onSelect(profile)}
				>
					<div className="font-medium">{profile.name}</div>
					<div className="text-sm text-muted-foreground">{profile.email}</div>
					<div className="text-sm text-muted-foreground">{profile.paymentMethod}</div>
				</div>
			))}
		</div>
	)
}

function BillingProfileRecordPanel({
	onClose,
	profile,
	isNew = false,
}: {
	onClose: () => void
	profile?: BillingProfile
	isNew?: boolean
}) {
	const title = isNew ? 'Add Billing Profile' : 'Billing Profile Details'

	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>{title}</DrawerTitle>
				<DrawerClose asChild>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="hover:bg-primary hover:text-primary-foreground"
					>
						<X className="h-4 w-4" />
					</Button>
				</DrawerClose>
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				{isNew ? (
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							Create a new billing profile to manage payments and subscriptions.
						</p>
					</div>
				) : (
					<div className="space-y-4">
						<div>
							<strong>Name:</strong> {profile?.name}
						</div>
						<div>
							<strong>Email:</strong> {profile?.email}
						</div>
						<div>
							<strong>Payment Method:</strong> {profile?.paymentMethod}
						</div>
						<div>
							<strong>Created:</strong> {profile?.createdAt}
						</div>
					</div>
				)}
			</div>

			{isNew && (
				<DrawerFooter>
					<Button onClick={onClose} className="w-full">
						Create Billing Profile
					</Button>
				</DrawerFooter>
			)}
		</div>
	)
}

export function BillingProfilesList() {
	const { state, openCreate, openRecord, closePanel, setView } =
		useSettingsPanelState('billing-profiles')

	// Temporary local state until wired to API
	const [profiles] = useState<BillingProfile[]>([])

	// Mock subscription state - no backend integration yet
	const hasSubscription = false

	const selected = state.recordId ? profiles.find((p) => p.id === state.recordId) : undefined

	const navigate = useNavigate()
	const workspaceUrl = useWorkspaceUrl()

	const handleAdd = () => openCreate()

	const handleSelect = (profile: BillingProfile) => openRecord(profile.id)
	const handleSignUp = () => {
		navigate(`${workspaceUrl}/sign-up`)
	}

	const listPanel = (
		<div className="flex flex-col h-full">
			<div className="flex items-center justify-between mb-4">
				<div className="text-lg font-semibold">Billing Profiles</div>
				<div className="flex items-center gap-3">
					<div className="flex border rounded-md">
						<Button
							variant={state.view === 'table' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('table')}
							className="rounded-r-none border-r"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button
							variant={state.view === 'cards' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('cards')}
							className="rounded-l-none"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>
					{hasSubscription ? (
						<Button onClick={handleAdd} size="sm" className="cursor-pointer">
							<Plus className="h-4 w-4 mr-2" />
							Add Profile
						</Button>
					) : (
                        <Button onClick={handleSignUp} className="cursor-pointer">
							Sign Up
						</Button>
					)}
				</div>
			</div>
			<div className="flex-1 overflow-auto">
				{state.view === 'table' ? (
					<BillingProfilesTable
						profiles={profiles}
						onSelect={handleSelect}
						onAdd={handleAdd}
						selectedId={state.recordId || undefined}
						hasSubscription={hasSubscription}
					/>
				) : (
					<BillingProfilesCards
						profiles={profiles}
						onSelect={handleSelect}
						onAdd={handleAdd}
						selectedId={state.recordId || undefined}
						hasSubscription={hasSubscription}
					/>
				)}
			</div>
		</div>
	)

	return (
		<div className="flex flex-col h-full">
			{listPanel}
			<Drawer open={state.isOpen} onOpenChange={(open) => !open && closePanel()} direction="right">
				<DrawerContentRight>
					<BillingProfileRecordPanel
						onClose={closePanel}
						profile={selected}
						isNew={state.mode === 'new'}
					/>
				</DrawerContentRight>
			</Drawer>
		</div>
	)
}
